import type {
  ColorByApplyTarget,
  ColorByRule,
} from 'components/color-by-attribute-modal';

/**
 * The four surfaces the Well page can paint — mock v13's, with its own target ids.
 *
 * The last three are the Subsurface Explorer's views, which also colour themselves from
 * the backend's `color_by`. A rule resolves on top of that colour, so an explicit rule
 * beats the page default instead of the two fighting.
 */
export const WELL_COLOR_TARGETS: ColorByApplyTarget[] = [
  { id: 'map', label: 'Map' },
  { id: 'scene', label: '3D View' },
  { id: 'profile', label: '2D Profile' },
  { id: 'gunBarrel', label: 'Gun Barrel' },
];

/**
 * The workspace's five. It adds the well list itself, which the Well page has no
 * equivalent of — one well is not a list — so the two hosts differ by exactly this.
 */
export const WORKSPACE_COLOR_TARGETS: ColorByApplyTarget[] = [
  { id: 'properties', label: 'Wells (map + list)' },
  ...WELL_COLOR_TARGETS,
];

/**
 * A new rule paints everywhere its host can until the reader narrows it.
 * @param {ColorByApplyTarget[]} targets The host's surfaces.
 * @return {Record<string, boolean>} Every one of them on.
 */
export const newRuleApplyTo = (
  targets: ColorByApplyTarget[]
): Record<string, boolean> =>
  Object.fromEntries(targets.map((target) => [target.id, true]));

/**
 * Whether a surface is already spoken for by another enabled rule.
 *
 * Two rules painting one view would fight, so the modal disables a target another rule
 * owns. A disabled rule holds nothing, and the rule being edited never blocks itself.
 * @param {ColorByRule[]} rules The current rule stack.
 * @param {string | null} editingRuleId The rule being edited, if any.
 * @return {Function} A predicate the modal calls per target.
 */
export const targetTakenBy =
  (rules: ColorByRule[], editingRuleId: string | null) =>
  (_attributeId: string, targetId: string): boolean =>
    rules.some(
      (rule) =>
        rule.ruleId !== editingRuleId &&
        rule.enabled &&
        rule.applyTo[targetId] === true
    );
